import { expectDefined, NumberSchema, type Interpable, type Lerpable } from '@utils';
import { z } from 'zod';
import { Angle } from './Angle';
import type { IPath, ParamPoint } from './IPath';
import { LineSegment, projectPointOntoLine } from './LineSegment';
import { Point, pointArraysAreEqual } from './Point';
import { Rect } from './Rect';
import { lineSegment, point } from './constructors';
import { composeSvgPathData, tokenizeSvgPathData } from './svgUtils';

// #region Schema

export const PolyLineSchema = z
  .array(NumberSchema, {
    message: 'PolyLine must be an even-length array of pairs, [x1, y1, x2, y2...]'
  })
  .transform(points => polyline(points));

// #endregion

export function polyline(points: Point[] | number[]) {
  return new PolyLine(points);
}
export class PolyLine implements IPath, Lerpable, Interpable {
  #points: Point[];
  #cache: { length?: number; bounds?: Rect } | undefined = undefined;

  // #region Constructors

  // Constructor takes an array of points or a flat array of x, y coordinates.
  constructor(points: Point[] | number[]) {
    this.#points = [];

    if (points.length === 0) {
      return;
    }

    if (typeof points[0] === 'number') {
      const numbers = points as number[];
      if (numbers.length % 2 !== 0) {
        throw new Error('PolyLine array must have an even number of elements');
      }
      for (let i = 0; i < points.length; i += 2) {
        this.#points.push(point(numbers[i], numbers[i + 1]));
      }
    } else {
      this.#points = points as Point[];
    }
  }

  // #endregion

  // #region Attributes

  get points(): Point[] {
    return this.#points;
  }

  get numberOfPoints(): number {
    return this.points.length;
  }

  get numberOfSegments(): number {
    return Math.max(0, this.points.length - 1);
  }

  get isEmpty(): boolean {
    return this.points.length === 0;
  }

  get start(): Point {
    if (this.isEmpty) {
      throw new Error('Cannot get start of empty PolyLine');
    }
    return this.points[0];
  }

  get end(): Point {
    if (this.isEmpty) {
      throw new Error('Cannot get end of empty PolyLine');
    }
    return this.points[this.points.length - 1];
  }

  private get _length(): number {
    if (this.isEmpty) {
      throw new Error('Cannot get length of empty PolyLine');
    }

    if (this.points.length < 2) return 0;

    let totalLength = 0;
    for (let i = 1; i < this.points.length; i++) {
      totalLength += this.points[i - 1].distanceTo(this.points[i]);
    }

    return totalLength;
  }

  private get _bounds(): Rect {
    if (this.isEmpty) {
      throw new Error('Cannot get bounds of empty PolyLine');
    }
    return Rect.fromPoints(this.points);
  }

  get length(): number {
    if (this.isEmpty) {
      throw new Error('Cannot get length of empty PolyLine');
    }
    if (this.#cache?.length === undefined) {
      const length = this._length;
      this.#cache = { ...(this.#cache ?? {}), length };
    }
    return expectDefined(this.#cache.length);
  }

  get bounds(): Rect {
    if (this.isEmpty) {
      throw new Error('Cannot get bounds of empty PolyLine');
    }
    if (this.#cache?.bounds === undefined) {
      const bounds = this._bounds;
      this.#cache = { ...(this.#cache ?? {}), bounds };
    }
    return expectDefined(this.#cache.bounds);
  }

  segmentAtIndex(index: number): LineSegment {
    if (index < 0 || index >= this.points.length - 1) {
      throw new Error('Segment index out of range');
    }
    return lineSegment(this.points[index], this.points[index + 1]);
  }

  get firstSegment(): LineSegment {
    return this.segmentAtIndex(0);
  }

  get lastSegment(): LineSegment {
    return this.segmentAtIndex(this.points.length - 2);
  }

  recalculateCache(): void {
    this.#cache = {
      length: this._length,
      bounds: this._bounds
    };
  }

  // #endregion

  // #region Comparison

  equals(other: PolyLine): boolean {
    return this === other || pointArraysAreEqual(this.points, other.points);
  }

  isSamePath(other: IPath): boolean {
    return other instanceof PolyLine && this.equals(other);
  }

  isIsomorphicTo(other: IPath): boolean {
    return other instanceof PolyLine;
  }

  // #endregion

  // #region Interpolation

  lerp(t: number, end: this): this {
    if (this.points.length !== end.points.length) {
      throw new Error('PolyLines must have the same number of points');
    }
    return polyline(this.points.map((p, i) => p.lerp(t, end.points[i]))) as this;
  }

  interp(end: this): (t: number) => this {
    return (t: number) => this.lerp(t, end);
  }

  // #endregion

  // #region Geometric Operations

  reversed(): this {
    return polyline([...this.points].reverse()) as this;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  flattened(flatness: number): ParamPoint[] {
    if (this.isEmpty) {
      return [];
    }

    const accumLengths = [0];
    for (let i = 1; i < this.points.length; i++) {
      accumLengths.push(
        accumLengths[i - 1] + this.points[i - 1].distanceTo(this.points[i])
      );
    }
    const length = accumLengths[accumLengths.length - 1];
    return accumLengths.map((l, i) => ({ p: this.points[i], t: l / length }));
  }

  segmentAt(t: number): {
    line: LineSegment;
    lineT: number;
    lengthOfPreviousSegments: number;
  } {
    if (this.isEmpty) {
      throw new Error('Cannot get segment of empty PolyLine');
    }

    const totalLength = this.length;

    if (t <= 0) {
      return { line: this.firstSegment, lineT: 0, lengthOfPreviousSegments: 0 };
    }

    if (t >= 1) {
      const lastSegment = this.lastSegment;
      return {
        line: lastSegment,
        lineT: 1,
        lengthOfPreviousSegments: totalLength - lastSegment.length
      };
    }

    let targetDistance = t * totalLength;
    let lengthOfPreviousSegments = 0;

    for (let i = 1; i < this.points.length; i++) {
      const segmentStart = this.points[i - 1];
      const segmentEnd = this.points[i];
      const segmentLength = segmentStart.distanceTo(segmentEnd);

      if (targetDistance <= segmentLength) {
        const lineT = targetDistance / segmentLength;
        return {
          line: lineSegment(segmentStart, segmentEnd),
          lineT,
          lengthOfPreviousSegments
        };
      }

      targetDistance -= segmentLength;
      lengthOfPreviousSegments += segmentLength;
    }

    throw new Error('Invalid PolyLine segment');
  }

  lengthAt(t: number): number {
    const { lengthOfPreviousSegments, line, lineT } = this.segmentAt(t);
    return lengthOfPreviousSegments + line.length * lineT;
  }

  pointAt(t: number): Point {
    const { line, lineT } = this.segmentAt(t);
    return line.pointAt(lineT);
  }

  derivative(t: number): Point {
    const { line, lineT } = this.segmentAt(t);
    return line.derivative(lineT);
  }

  normal(t: number): Point {
    const d = this.derivative(t);
    return point(-d.y, d.x).normalized();
  }

  angle(t: number): Angle {
    const { line, lineT } = this.segmentAt(t);
    return line.angle(lineT);
  }

  project(p: Point): { p: Point; t: number; d: number } {
    if (this.isEmpty) {
      throw new Error('Cannot project onto empty PolyLine');
    }

    let closestPoint = this.points[0];
    let closestT = 0;
    let closestDistance = Infinity;

    for (let i = 1; i < this.points.length; i++) {
      const p1 = this.points[i - 1];
      const p2 = this.points[i];
      const projection = projectPointOntoLine(p, p1, p2);
      const distance = projection.d;
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPoint = projection.p;
        closestT = projection.t;
      }
    }

    return { p: closestPoint, t: closestT, d: closestDistance };
  }

  /**
   * Determines whether this polyline intersects the given rectangle.
   *
   * @param r The rectangle.
   *
   * @returns True if the polyline intersects the rectangle, false otherwise.
   */
  intersectsRect(r: Rect): boolean {
    if (this.isEmpty) {
      return false;
    }

    if (this.points.length === 1) {
      return r.containsPoint(this.points[0]);
    }

    for (let i = 0; i < this.numberOfSegments; i++) {
      if (this.segmentAtIndex(i).intersectsRect(r)) {
        return true;
      }
    }

    return false;
  }

  // #endregion

  // #region Serialization

  toJSON(): unknown {
    // Format as a flat array of numbers [x1, y1, x2, y2...]
    return this.points.flatMap(p => [p.x, p.y]);
  }

  static fromJSON(json: unknown): PolyLine {
    return PolyLineSchema.parse(json);
  }

  // #endregion

  // #region SVG Conversion

  toSvgPathData(fractionDigits?: number, includeMove = true): string {
    let components: (string | number)[] = [];
    if (includeMove) {
      components = ['M', this.points[0].x, this.points[0].y];
    }
    for (let i = 1; i < this.points.length; i++) {
      components.push('L', this.points[i].x, this.points[i].y);
    }
    return composeSvgPathData(components, fractionDigits);
  }

  static fromSvgPathData(pathData: string): PolyLine {
    const tokens = tokenizeSvgPathData(pathData);

    let i = 0;

    function hasNextToken(): boolean {
      return i < tokens.length;
    }

    function nextToken(): string | number {
      if (i >= tokens.length) {
        throw new Error('Invalid SVG polyline data: Not enough tokens');
      }
      return tokens[i++];
    }

    const moveTo = nextToken();
    if ((moveTo as string) !== 'M') {
      throw new Error('Invalid SVG polyline data: Expected move command.');
    }
    const points = [];
    const x1 = nextToken() as number;
    const y1 = nextToken() as number;
    points.push(point(x1, y1));

    while (hasNextToken()) {
      const lineTo = nextToken();
      if ((lineTo as string) !== 'L') {
        throw new Error('Invalid SVG polyline data: Expected line command.');
      }
      const x = nextToken() as number;
      const y = nextToken() as number;
      points.push(point(x, y));
    }

    return polyline(points);
  }

  // #endregion

  // #region String Conversion

  /**
   * Formats the `PolyLine` as a string for debugging.
   */
  toString(fractionDigits?: number): string {
    return `[${this.points.map(p => p.toString(fractionDigits)).join(', ')}]`;
  }
}
